import { MobilePassData } from '../base/n3xus/types'
import {FAN_PASS_SESSION_STORAGE_KEY} from '../constants/components'

/**
 * Represents an error object with a message property.
 * 
 * @property {string} message - The error message.
 */
type ErrorWithMessage = {
	message: string
}

export type FpSession = { 
	issueTS: number, 
	token: string, 
	didToken: string, 
	apiKey: string,
	mobilePass?: Array<MobilePassData> | null,
	wallet?: string | null
	email?: string | null
	expiryDate?: string | null
}

/**
 * Type guard to check if an unknown error object contains a message property of type string.
 *
 * @param error - The unknown error object to check.
 * @returns A boolean indicating whether the error object has a message property of type string.
 */
const isErrorWithMessage = (error: unknown): error is ErrorWithMessage => {
	return (
		typeof error === 'object' &&
		error !== null &&
		'message' in error &&
		typeof (error as Record<string, unknown>).message === 'string'
	)
}

/**
 * Converts an unknown value to an ErrorWithMessage object.
 * 
 * @param maybeError - The value that might be an error.
 * @returns An ErrorWithMessage object.
 * 
 * If the input value is already an ErrorWithMessage, it is returned as is.
 * If the input value is not an ErrorWithMessage, it attempts to convert it to a string
 * using JSON.stringify. If that fails (e.g., due to circular references), it falls back
 * to using String(maybeError).
 */
const toErrorWithMessage = (maybeError: unknown): ErrorWithMessage =>{
	if (isErrorWithMessage(maybeError)) return maybeError

	try {
		return new Error(JSON.stringify(maybeError))
	} catch {
		// fallback in case there's an error stringifying the maybeError
		// like with circular references for example.
		return new Error(String(maybeError))
	}
}

/**
 * Retrieves the error message from an unknown error object.
 *
 * @param error - The error object from which to extract the message.
 * @returns The error message as a string.
 */
export const getErrorMessage = (error: unknown): string => {
	return toErrorWithMessage(error).message
}

/**
 * Retrieves the stored session from the session storage.
 *
 * @returns {FpSession | null} The stored session if it exists, otherwise null.
 */
export const getLatestSession = (apiKey: string): FpSession | null => {
	try {
		const sessions = sessionStorage.getItem(sessionKey)
		if (!sessions) return null

		const parsedSessions = JSON.parse(sessions)
		
		if (!Array.isArray(parsedSessions) || parsedSessions.length === 0) return null
		
		// Filter sessions by apiKey and sort by issueTS in descending order (newest first)
		const matchingSessions = parsedSessions
			.filter(session => session.apiKey === apiKey)
			.sort((a, b) => b.issueTS - a.issueTS)

		// Return the first (latest) matching session, or null if no matches found
		return matchingSessions.length > 0 ? matchingSessions[0] : null
	} catch (error) {
		console.error(getErrorMessage(error))
		return null
	}
}


/**
 * Stores the given session string in the session storage under the key `FAN_PASS_SESSION_STORAGE_KEY`.
 *
 * @param session - The session string to be stored.
 * @returns void
 */
export const addSession = (session: FpSession): void => {
	try {
		const key = sessionKey
		const sessions = sessionStorage.getItem(key)
		if (sessions) {
			const parsedSessions = JSON.parse(sessions)
			const updatedData = parsedSessions.map((obj: FpSession) => 
				obj.apiKey === session.apiKey ? session : obj
			);
			sessionStorage.setItem(key, JSON.stringify(updatedData))
		} else {
			sessionStorage.setItem(key, JSON.stringify([session]))
		}
	} catch (error) {
		console.error(getErrorMessage(error))	
	}
}

export const domainToHandle = (url:string) => {
    try {
        const domain = new URL(url).hostname.replace(/\./g, "");
        return domain;
    } catch (error) {
        console.error("Invalid URL:", error);
        return null;
    }
}

/**
 * Clears the stored session from the session storage.
 *
 * This function removes the item associated with the key `FAN_PASS_SESSION_STORAGE_KEY`
 * from the session storage, effectively clearing the stored session data.
 *
 * @returns {void}
 */
export const clearStoredSession = (): void => {
	try {
		sessionStorage.removeItem(sessionKey);	
	} catch (error) {
		console.error(getErrorMessage(error))
	}
}

export const sessionKey = `${FAN_PASS_SESSION_STORAGE_KEY}${domainToHandle(window.location.href)}`

// The code above sorts sessions in descending order (newest first),
// where higher issueTS values appear before lower ones.

export type LogoSet = {
		large: {
			src: string,
		},
		medium: {
			src: string,
		},
		small: {
			src: string,
		},
		alt: string,
	}

export const preloadLogos = (logos: LogoSet) => {
	try {
		if (typeof logos === 'object') {
			(Object.keys(logos) as (keyof LogoSet)[]).forEach((key) => {
				if (
					typeof logos[key] === 'object' &&
					logos[key] && 'src' in logos[key]
				) {
					const img = new Image()
					img.src = logos[key].src
				}
			})
		}            
	} catch (error) {
		console.warn('Error loading logo images', error)
	}
}

export const detectMobilePlatform = (): string => {
    const userAgent = navigator.userAgent || navigator.vendor || ('opera' in window && window.opera) as string;

    // iOS specific detection (iPhone/iPod or iPad)
    if (/iPhone|iPod/i.test(userAgent)) {
        return 'iOS Phone';
    }

    if (/iPad/i.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
        // Detect iPads on iOS 13+ (since they use MacIntel platform with touch support)
        return 'iOS Tablet';
    }

    // Android specific detection (Phone or Tablet)
    if (/Android/i.test(userAgent)) {
        return /mobile/i.test(userAgent) ? 'Android Phone' : 'Android Tablet';
    }

    // Default fallback for other platforms
    return 'Other';
};

export const findBoxNode = (root: ShadowRoot | HTMLElement): HTMLElement | null => {
	const box = root.querySelector('.box.box--fixed-padding');
	if (box) return box as HTMLElement;
	// Recursively search shadow roots of child elements
	for (const el of Array.from(root.children)) {
	  if ((el as HTMLElement).shadowRoot) {
		const found = findBoxNode((el as HTMLElement).shadowRoot!);
		if (found) return found;
	  }
	}
	return null;
}